import {
  Link,
  TextField,
} from '@mui/material';
import { Formik } from 'formik';
import { AnimatePresence, motion } from 'framer-motion';
import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import ReactDOM from 'react-dom';
import * as Yup from 'yup';

import analytics, { useSegmentEmail } from '../../app/analytics';
import { useUserData } from '../../app/userDataProvider';
import { submitForm } from '../../helpers/hubspot';
import Button from '../form/button';

const FORM_ID = '1c01accc-e16c-467b-b4aa-9bd108446b4d';

const openPath = (path, email) => {
  // expand the url to include the full path
  let url;
  if (path.startsWith('/')) {
    url = new URL(`${window.location.origin}${path}`);
  } else {
    url = new URL(path);
  }

  // log CAD downloads
  if (path.includes('public.parallelfluidics.com/cad')) {
    analytics.downloadCad({
      userId_: 'not-used', /* string */
      email, /* string */
      downloadUrl: url.href, /* string */
    });
  } else {
    analytics.visitPaywalledContent({
      userId_: 'not-used', /* string */
      email, /* string */
      downloadUrl: url.href, /* string */
    });
  }

  // if the path is on the same hostname, open it in the same window
  // otherwise, open it in a new window
  if (url.hostname === window.location.hostname) {
    window.location.href = url.href;
  } else {
    window.open(url.href, '_blank');
  }
};

function Modal() {
  const [open, setOpen] = useState(false);
  const [nextURL, setNextURL] = useState(null);
  const [showCloseButton, setShowCloseButton] = useState(true);
  const { user } = useUserData();

  const segmentEmail = useSegmentEmail();
  const openDownloadModal = useCallback((url) => {
    const email = user?.email || segmentEmail;
    if (email) {
      const fields = [
        {
          name: 'download_url',
          value: url,
        },
      ];
      if (email) {
        fields.push({
          name: 'email',
          value: email,
        });
      }
      submitForm(FORM_ID, fields, () => {
        if (url) { openPath(url, email); }
      });
    } else {
      if (url) {
        setShowCloseButton(true);
        setNextURL(url);
      } else {
        setShowCloseButton(false);
        setNextURL(null);
      }
      setOpen(true);
    }
  }, [user, segmentEmail]);

  // any button tagged with 'download-button'
  useEffect(() => {
    const links = window.$('[data-pf-paywall="button"]');
    const eventListeners = [];
    for (let i = 0; i < links.length; i += 1) {
      const link = links[i];
      const oldHref = link.getAttribute('href');
      if (oldHref !== '#') {
        link.setAttribute('data-href', oldHref);
      }
      const newHref = link.getAttribute('data-href');
      if (newHref) {
        link.setAttribute('href', '#');
        const onClick = (e) => {
          e.preventDefault();
          openDownloadModal(newHref);
        };
        link.addEventListener('click', onClick);
        eventListeners.push({ link, onClick });
      }
    }
    return () => {
      for (let i = 0; i < eventListeners.length; i += 1) {
        const { link, onClick } = eventListeners[i];
        link.removeEventListener('click', onClick);
      }
    };
  }, [openDownloadModal]);

  const validationSchema = Yup.object().shape({
    email: Yup.string().email('Email is invalid').required('Email is required'),
  });

  const signInURL = useMemo(() => {
    let url = `/account/sign-in?next=${encodeURIComponent(window.location.pathname + window.location.search)}`;
    if (nextURL) {
      url += `&open=${encodeURIComponent(nextURL)}`;
    }
    return url;
  }, [nextURL]);

  return ReactDOM.createPortal(
    (
      <AnimatePresence>
        {open ? (
          <motion.div
            className="onboarding-wrap"
            style={{ display: 'flex' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {showCloseButton ? (
              <button type="button" onClick={() => { setOpen(false); }} className="onboarding-close">close</button>
            ) : null}
            <div className="onboarding-slider w-slider" style={{ width: '40rem' }}>
              <div className="onboarding-mask w-slider-mask">
                <div className="w-slide">
                  <div className="onboarding-card">
                    <div className="onboarding-content" style={{ padding: '1.5em 1em' }}>
                      <div className="margin-bottom margin-base">
                        <div className="w-layout-grid column-base">
                          <div className="content-left">
                            <h4 className="font-title margin-bottom">Enter your email below</h4>
                            <p>
                              Or
                              {' '}
                              <Link href={signInURL}>sign in</Link>
                              {' '}
                              to continue.
                            </p>
                          </div>
                        </div>
                      </div>
                      <Formik
                        initialValues={{ email: '' }}
                        validationSchema={validationSchema}
                        validateOnChange={false}
                        onSubmit={(values, { setSubmitting }) => {
                          submitForm(FORM_ID, [
                            {
                              name: 'email',
                              value: values.email,
                            },
                            {
                              name: 'download_url',
                              value: nextURL || document.location.href,
                            },
                          ], () => {
                            if (nextURL) {
                              openPath(nextURL, values.email);
                            }
                            setOpen(false);
                            setNextURL(null);
                            setSubmitting(false);
                          });
                        }}
                      >
                        {({
                          handleSubmit, handleBlur, handleChange, values, isSubmitting, errors,
                        }) => (
                          <div className="form-vertical">
                            <div className="w-layout-grid sign-up-field-list">
                              <div className="sign-up-field-item">
                                <TextField
                                  placeholder="Work email"
                                  id="email"
                                  error={!!errors.email}
                                  helperText={errors.email}
                                  onChange={handleChange}
                                  onBlur={handleBlur}
                                  value={values.email}
                                  disabled={isSubmitting}
                                />
                              </div>
                            </div>
                            <div className="content-centered">
                              <Button
                                disabled={isSubmitting}
                                label="Continue"
                                onClick={handleSubmit}
                              />
                            </div>
                          </div>
                        )}
                      </Formik>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    ),
    document.body,
  );
}

export default Modal;
